/**
 * @mixin
 * this mixin is extending the ParamTypeExtenderDataInputMenuMixin and adds external source options to user selection for this param
 *
 * Extended mixins: [ParamTypeMixin, ParamTypeExtenderDataInputMenuMixin]
 */

import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';

import ParamTypeExtenderDataInputMenuMixin from './ParamTypeExtenderDataInputMenuMixin';
import ParamTypeMixin from './ParamTypeMixin';
import ExternalSourceMixinService from './external-source-mixin-service';
import { EXTERNAL_SOURCE_TAB_KEY } from '../../../../DataInput/DataInputMenu/helpers/data-input-menu.const';
import { configurationsValuesByFieldUUID } from '@/store/modules/configurationEditor/getters';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getConfigurationFieldsIdsPerInterface, getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import AppService from '@/utils/AppService';
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
/**
 * depended on the assumption that the ParamType using this mixin also uses ParamTypeMixin
 * for use of props ('param', 'node', etc..)
 */

export default {
	mixins: [ParamTypeMixin, ParamTypeExtenderDataInputMenuMixin],
	computed: {
		...mapGetters([
			getConfigurationFieldsIdsPerInterface.fullPath,
			getConfigurationFieldResponse.fullPath,
			isConfigurationsOn.fullPath,
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
			configurationsValuesByFieldUUID.fullPath,
		]),
		hasExternalSource() {
			return !!this.param?.source;
		},
		userSchemaMappingId() {
			// Being used by external-source-mixin-service to determine from where to get options.
			return this.node.user_schema_mappings_source_field_uuid;
		},
		placementContextId() {
			// Being used by external-source-mixin-service to determine from where to get options.
			return this.node.placementContextId;
		},
		needGetOptionsForAction() {
			const dependsOn = this.param.depends_on || [];
			let paramsMap = this.node.params_map;

			if (this.node.user_schema_mappings_source_field_uuid) {
				paramsMap = WorkflowNodeService.extendParamsMapWithParamsFromDynamicMapperIfExists(
					this.node,
					this[getConfigurationAndInterfaceIdByFieldUUID.fullPath],
					this[getConfigurationFieldResponse.fullPath],
					this[isConfigurationsOn.fullPath],
					this.node.params_map,
					this[configurationsValuesByFieldUUID.fullPath]
				);
			}

			// all the params of depended keys are already set
			return dependsOn.every((paramKey) => {
				const paramValue = paramsMap[paramKey];
				return !isNullOrUndefinedOrEmpty(paramValue);
			});
		},
		cantGetOptionsForAction() {
			const credsId = WorkflowNodeService.getNodeRelevantCredsId(
				this.workflowVersion,
				this.node,
				this[getConfigurationFieldResponse.fullPath],
				this[isConfigurationsOn.fullPath]
			);
			return !this.service || (isNullOrUndefinedOrEmpty(credsId) && AppService.doesRequiresAuth(this.service));
		},
		// to be used when items + some extra logical items need to be used, * optional to be override by another mixin (for example: UserMatchingMixin)
		allItems() {
			return this.$data._items;
		},
		fromExternalSource() {
			return this.curTabCode === EXTERNAL_SOURCE_TAB_KEY;
		},
		externalDataInputMenuProps() {
			return {
				loading: this.$data._loading,
				items: this.allItems,
				hasMoreData: this.$data._hasMoreData,
				retrievalErrorMessage: this.retrievalErrorMessage,
			};
		},
		retrievalErrorMessage() {
			return this.$data._errorMessage;
		},
		searchString() {
			return this.$data._search;
		},
	},
	methods: {
		_getData() {}, // just a placeholder, definition on created
		_getDataOnDemand() {}, // just a placeholder, definition on created
		updateExternalLocalValue(payload) {
			this.updateConstantValueFromDataInputMenu(payload);
		},
		resetState() {
			this.$data._page = 1;
			this.$data._hasMoreData = false;
		},
		refreshData() {
			this.resetState();
			this._getDataOnDemand();
		},
		/**
		 * to be used by param to know when user scrolled to bottom of list
		 * @param {*} entries
		 * @param {*} observer
		 * @param {*} isIntersecting
		 */
		endIntersect(entries, observer, isIntersecting) {
			if (isIntersecting) {
				ExternalSourceMixinService.seeMore.call(this);
			}
		},
		clearValue() {
			this.updateExternalLocalValue({ value: '', option: '' });
		},
	},
	created() {
		this._getData = debounce(ExternalSourceMixinService.retrieveData.bind(this), 600);
		this._getDataOnDemand = ExternalSourceMixinService.retrieveData.bind(this);
	},
	mounted() {
		// if current param has external source ('source' key in param is defined) retrieve its options
		if (this.hasExternalSource) {
			this._getDataOnDemand();
			ExternalSourceMixinService.addExternalTabItem.call(this);
			ExternalSourceMixinService.addExternalDataInputListeners.call(this);
		}
	},
	data() {
		return {
			_hasMoreData: false,
			_retrievalId: null,
			_page: 1,
			_loading: false,
			_search: '',
			_items: [],
			_errorMessage: null,
		};
	},
	watch: {
		cantGetOptionsForAction: {
			handler(newV, oldV) {
				// longest name ever - maybe should just be a comment
				// if we now able to get options for action became true
				const cantGetOptionsForActionChangedToFalse = !newV && oldV;
				if (cantGetOptionsForActionChangedToFalse) {
					this._getData();
				}
			},
		},
		externalDataInputMenuProps: {
			handler() {
				ExternalSourceMixinService.setExternalDataInputMenuProps.call(this);
			},
			deep: true,
			immediate: true,
		},
		searchString: {
			handler() {
				if (this.param.searchable) {
					ExternalSourceMixinService.onSearchStringUpdate.call(this);
				}
			},
		},
		allItems: {
			handler(items) {
				this.applyLabelFromOptions(items);
			},
		},
	},
};
